import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const DynamicBrandInfoSection = ({
  brandInfo,
  scrollProgress,
  palette,
  profileId,
  tabNavigation,
  activeTab,
  backgroundImage,
  logoUrl,
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // Animation values based on scroll
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 50]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const bgScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  // Get brand information with existence checks
  const hasBrandName = "Brand Name" in brandInfo;
  const hasTagline = "Tagline" in brandInfo;
  const hasDescription = "Brand Description" in brandInfo;
  const hasLogoUrl = "Logo URL" in brandInfo;
  const hasWebsiteUrl = "Website URL" in brandInfo;

  const brandName = hasBrandName
    ? brandInfo["Brand Name"] || "Brand Name"
    : null;
  const tagline = hasTagline ? brandInfo["Tagline"] : null;
  const description = hasDescription ? brandInfo["Brand Description"] : null;
  const logoUrlFromBrandInfo = hasLogoUrl ? brandInfo["Logo URL"] : null;
  const websiteUrl = hasWebsiteUrl ? brandInfo["Website URL"] : null;

  // Prioritize uploaded logo over questionnaire Logo URL field
  const finalLogoUrl = logoUrl || logoUrlFromBrandInfo;

  // Ensure website URL has proper protocol
  const formatWebsiteUrl = (url) => {
    if (!url) return null;

    // Remove any leading/trailing whitespace
    const cleanUrl = url.trim();

    // If URL already has protocol, return as is
    if (cleanUrl.startsWith("http://") || cleanUrl.startsWith("https://")) {
      return cleanUrl;
    }

    // Add https:// as default protocol
    return `https://${cleanUrl}`;
  };

  const formattedWebsiteUrl = formatWebsiteUrl(websiteUrl);

  // Extract RGB values from primary color for gradients
  const getRgb = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    return { r, g, b };
  };

  const primaryRgb = getRgb(palette.primary);
  const primaryColorRgb = `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`;

  // Determine the section's height based on active tab
  // For non-discovery tabs, we'll use a reduced height to keep it as a header
  const isShrunk = activeTab !== "discovery";
  const sectionHeight = isShrunk ? "auto" : "min-h-screen";
  const sectionPadding = isShrunk ? "py-6" : "";

  // This will add a shadow to the bottom of the shrunk header
  const bottomShadow = isShrunk ? "shadow-lg border-b border-white/5" : "";

  // Debug: Log the backgroundImage prop
  console.log("[DynamicBrandInfoSection] Background image:", backgroundImage);

  return (
    <motion.section
      ref={sectionRef}
      className={`relative ${sectionHeight} ${sectionPadding} ${bottomShadow} flex flex-col justify-center items-center w-full overflow-hidden ${
        isShrunk ? "sticky top-0 z-50" : ""
      }`}
      style={{
        opacity: isShrunk ? 1 : opacity, // Always visible when shrunk
        scale: isShrunk ? 1 : scale, // No scale effect when shrunk
        y: isShrunk ? 0 : y, // No y movement when shrunk
        backgroundColor: "#111111", // Dark background for better contrast
      }}
    >
      {/* Background image with parallax effect */}
      {backgroundImage ? (
        <motion.div
          className="absolute inset-0 w-full h-full bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            y: isShrunk ? 0 : bgY,
            scale: isShrunk ? 1 : bgScale,
            opacity: 0.9, // Increased opacity significantly for better visibility
            filter: "contrast(1.1) brightness(0.9)", // Less darkening
            mixBlendMode: "normal", // Changed to normal for better visibility
          }}
          onLoad={() =>
            console.log("[DynamicBrandInfoSection] Background image loaded")
          }
          onError={() =>
            console.log(
              "[DynamicBrandInfoSection] Background image failed to load"
            )
          }
        />
      ) : (
        <div className="absolute inset-0 w-full h-full bg-red-500 opacity-30">
          {/* Debug: Red background when no hero image */}
        </div>
      )}

      {/* Gradient overlay */}
      <motion.div
        className="absolute inset-0 w-full h-full"
        style={{
          y: isShrunk ? 0 : bgY,
          backgroundImage: `radial-gradient(circle at 50% 50%, rgba(${primaryColorRgb}, 0.2) 0%, rgba(0, 0, 0, 0.4) 70%)`,
          opacity: 0.5, // Reduced opacity significantly to let image show through
          mixBlendMode: "normal",
        }}
      />

      <div
        className={`relative z-10 w-full px-6 text-center ${
          isShrunk ? "py-4" : ""
        }`}
      >
        {/* Logo */}
        {finalLogoUrl && !isShrunk && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12 flex justify-center"
          >
            <img
              src={finalLogoUrl}
              alt={`${brandName || "Brand"} logo`}
              className="max-h-32 max-w-full object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
          </motion.div>
        )}

        {/* For shrunk state, create a compact header with logo and name side by side */}
        {isShrunk && finalLogoUrl && (
          <div className="flex items-center justify-center mb-6">
            <img
              src={finalLogoUrl}
              alt={`${brandName || "Brand"} logo`}
              className="h-12 mr-4 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
            {brandName && (
              <h1 className="text-2xl font-bold text-white">{brandName}</h1>
            )}
          </div>
        )}

        {/* Brand Name - only show in full mode if not shrunk */}
        {brandName && !isShrunk && (
          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-6xl md:text-8xl leading-none mb-8 font-bold text-white"
            style={{
              textShadow: `0 2px 4px rgba(0,0,0,0.3)`,
            }}
          >
            {brandName}
          </motion.h1>
        )}

        {/* Tab Navigation - always visible */}
        <div className={`${isShrunk ? "mb-0" : "mb-12"}`}>{tabNavigation}</div>

        {/* Tagline - only show when not shrunk */}
        {tagline && !isShrunk && (
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-2xl md:text-3xl mb-8 text-white/70"
            style={{
              opacity: 0.9,
            }}
          >
            {tagline}
          </motion.h2>
        )}

        {/* Description - only show when not shrunk */}
        {description && !isShrunk && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mx-auto mb-12 max-w-3xl md:max-w-4xl"
          >
            <p className="text-lg text-white/70">{description}</p>
          </motion.div>
        )}

        {/* Website link - only show when not shrunk */}
        {formattedWebsiteUrl && !isShrunk && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mb-12"
          >
            <a
              href={formattedWebsiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-black/40 backdrop-blur-md hover:bg-black/60 border border-white/10 transition-all duration-300 shadow-lg"
              style={{
                color: "#ffffff",
                boxShadow: `0 4px 20px rgba(0, 0, 0, 0.3), 0 0 15px rgba(${primaryColorRgb}, 0.2)`,
              }}
            >
              <span>Visit Website</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 17L17 7M17 7H7M17 7V17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </motion.div>
        )}

        {/* Scroll indicator - only show when not shrunk */}
        {!isShrunk && (
          <motion.div
            className="animate-bounce"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
          >
            <div
              className="w-8 h-14 border-2 rounded-full mx-auto flex justify-center"
              style={{ borderColor: palette.primary }}
            >
              <div
                className="w-1.5 h-4 rounded-full mt-2"
                style={{ backgroundColor: palette.primary }}
              ></div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.section>
  );
};

export default DynamicBrandInfoSection;
